import { useTheme } from '@chakra-ui/react';
import { useFormApi } from '@data-driven-forms/react-form-renderer';
import { css } from '@emotion/react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SxProps } from '@mui/material/styles';
import { useState } from 'react';

import Box from '~/common/components/Box';
import IconButton from '~/common/components/Icons/IconButton';
import {
  Table as TableComponent,
  Tbody,
  Thead,
  Tr,
} from '~/common/components/Tables';

import GenericTooltip from '../components/GenericTooltip';
import { Icon } from './Icon';

type TableRowProps = {
  fields?: any[];
  headerCols?: {
    colSpan?: number;
  }[];
  sx?: SxProps;
};

export function TableRow({
  fields,
  headerCols,
  sx = {},
}: Readonly<TableRowProps>) {
  const { renderForm } = useFormApi();
  const { colors } = useTheme();

  return (
    <Tr
      sx={sx}
      css={css`
        :nth-of-type(even) {
          background-color: ${colors?.bgGray['100']};
        }
      `}
    >
      {fields.map((field, i) => (
        <td
          colSpan={headerCols[i]?.colSpan}
          css={css`
            font-size: 0.875rem;
            margin: 0;
          `}
          key={field.name}
        >
          {renderForm([field])}
        </td>
      ))}
    </Tr>
  );
}

type TableProps = {
  tableHeader: {
    colSpan?: number;
    label?: string;
    tooltip?: string;
  }[];
  fields?: any[];
  accordion?: boolean;
  isEditable?: boolean;
  headerBgColor?: string;
  sx?: any;
};

export default function Table({
  tableHeader,
  fields,
  accordion = false,
  isEditable = false,
  headerBgColor = 'gray.50',
  sx = {},
}: Readonly<TableProps>) {
  const { renderForm } = useFormApi();
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(true);

  return (
    <TableComponent
      size="small"
      variant="unstyled"
      css={css`
        border-radius: 4px;
        margin: 1rem 0;
        border-collapse: separate;
        border-spacing: 0;

        & th,
        & td {
          text-align: left;
          vertical-align: middle;
          padding: 0.5rem 1.5rem;
          min-height: 40px;
        }
      `}
      sx={{
        border: `1px solid ${colors?.gray['200']}`,
        ['td']: {
          verticalAlign: 'middle',
        },
        ...(sx?.verticalAlign && {
          ['td:not(:first-of-type)']: {
            verticalAlign: sx.verticalAlign,
          },
        }),
        ...sx,
      }}
    >
      <Thead
        sx={{
          position: 'relative',
          backgroundColor: isEditable ? colors?.blue[50] : headerBgColor,
        }}
      >
        {tableHeader.some((obj) => obj.label) && (
          <HeaderMapper tableHeader={tableHeader} />
        )}
        {accordion && (
          <IconButton
            aria-label="expand"
            size="sm"
            variant="ghost"
            icon={
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
            }
            onClick={() => setExpanded((e) => !e)}
            css={css`
              display: inline-block;
              position: absolute;
              top: 0.25rem;
              right: 1rem;
            `}
          />
        )}
      </Thead>
      {expanded && (
        <Tbody>
          {fields.map((field) => {
            field.headerCols = tableHeader;
            return renderForm([field]);
          })}
        </Tbody>
      )}
    </TableComponent>
  );
}

type HeaderMapperProps = {
  tableHeader: {
    colSpan?: number;
    label?: string;
    tooltip?: string;
    startIcon?: string;
    endIcon?: string;
  }[];
};

function HeaderMapper({ tableHeader }: Readonly<HeaderMapperProps>) {
  return (
    <Tr>
      {tableHeader.map(({ label, tooltip, colSpan, startIcon, endIcon }) => (
        <th
          scope="col"
          colSpan={colSpan}
          key={label}
          style={{ fontWeight: 500 }}
        >
          <Box display="flex" gap="8px">
            {startIcon && <HeaderIcon icon={startIcon} />}
            {label}
            {tooltip && <GenericTooltip label={tooltip} />}
            {endIcon && <HeaderIcon icon={endIcon} />}
          </Box>
        </th>
      ))}
    </Tr>
  );
}

type HeaderIconProps = {
  icon: string;
};

function HeaderIcon({ icon }: Readonly<HeaderIconProps>) {
  if (icon === 'manualReview') {
    return (
      <Icon
        iconType="faFlag"
        variant="round"
        color="black"
        bgColor="orange.300"
      />
    );
  }

  return <Icon iconType={icon} />;
}
